import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  type Timestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import type { AnonymousComplaint, AnonymousComplaintCategory, AnonymousComplaintStatus } from '@/types';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { Eye } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDateTimeMexicoCity } from '@/lib/dateUtils';

const CATEGORY_LABEL: Record<AnonymousComplaintCategory, string> = {
  violencia: 'Violencia laboral',
  riesgo_psicosocial: 'Riesgo psicosocial',
  entorno_organizacional: 'Entorno organizacional',
  otro: 'Otro',
};

const STATUS_LABEL: Record<AnonymousComplaintStatus, string> = {
  pending: 'Pendiente',
  review: 'En revisión',
  resolved: 'Resuelto',
};

const STATUS_VARIANT: Record<AnonymousComplaintStatus, 'outline'> = {
  pending: 'outline',
  review: 'outline',
  resolved: 'outline',
};

const STATUS_BADGE_CLASS: Record<AnonymousComplaintStatus, string> = {
  pending:
    'border-transparent bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300',
  review: 'border-transparent bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-300',
  resolved:
    'border-transparent bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300',
};

function formatCreatedAt(value: unknown): string {
  if (value && typeof (value as Timestamp).toDate === 'function') {
    return formatDateTimeMexicoCity((value as Timestamp).toDate());
  }
  if (typeof value === 'string' && value) {
    return formatDateTimeMexicoCity(value);
  }
  return '—';
}

const selectClassName = cn(
  'rounded-md border border-input bg-background px-2 py-1.5 text-xs sm:text-sm',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1'
);

export function ComplaintsAdminPage() {
  const { appUser, canWriteCompanyData } = useAuth();
  const companyId = appUser?.companyId ?? null;
  const [rows, setRows] = useState<AnonymousComplaint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [selectedComplaint, setSelectedComplaint] = useState<AnonymousComplaint | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const canQuery = useMemo(() => Boolean(companyId), [companyId]);

  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      setRows([]);
      return;
    }
    setLoading(true);
    setError(null);
    const q = query(
      collection(db, 'complaints'),
      where('companyId', '==', companyId),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list: AnonymousComplaint[] = snap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            companyId: (data.companyId as string) ?? '',
            category: data.category as AnonymousComplaintCategory,
            subject: (data.subject as string) ?? '',
            description: (data.description as string) ?? '',
            status: (data.status as AnonymousComplaintStatus) ?? 'pending',
            createdAt: data.createdAt as AnonymousComplaint['createdAt'],
            updatedAt: data.updatedAt as AnonymousComplaint['updatedAt'],
          };
        });
        setRows(list);
        setLoading(false);
      },
      (err: unknown) => {
        const code =
          err && typeof err === 'object' && 'code' in err ? String((err as { code: unknown }).code) : '';
        console.error('[complaints Firestore]', code || err, err);
        setError(
          code
            ? `No se pudieron cargar las denuncias (${code}). Revisa la consola (F12), índices y reglas de Firestore.`
            : 'No se pudieron cargar las denuncias. Revisa la consola (F12), tu conexión o vuelve a intentar.'
        );
        setLoading(false);
      }
    );
    return () => unsub();
  }, [companyId]);

  const handleStatusChange = async (complaintId: string, next: AnonymousComplaintStatus) => {
    if (!canWriteCompanyData) return;
    setRowErrors((prev) => {
      const nextMap = { ...prev };
      delete nextMap[complaintId];
      return nextMap;
    });
    setUpdatingId(complaintId);
    try {
      await updateDoc(doc(db, 'complaints', complaintId), {
        status: next,
        updatedAt: serverTimestamp(),
      });
    } catch {
      setRowErrors((prev) => ({
        ...prev,
        [complaintId]: 'No se pudo actualizar. Intenta de nuevo.',
      }));
    } finally {
      setUpdatingId(null);
    }
  };

  if (!canQuery) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold">Buzón de quejas</h1>
        <Card>
          <CardHeader>
            <CardTitle>Sin empresa asignada</CardTitle>
            <CardDescription>Tu usuario no tiene una empresa vinculada; no es posible consultar denuncias.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Buzón de quejas</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Denuncias anónimas recibidas por enlace público. Actualiza el estatus conforme avance el seguimiento interno.
        </p>
        {!canWriteCompanyData && (
          <p className="text-xs text-muted-foreground mt-1">
            Tu rol es solo lectura: puedes consultar las denuncias, pero no cambiar su estatus.
          </p>
        )}
      </div>

      {error && (
        <div
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Cargando denuncias…</p>
      ) : rows.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Sin reportes</CardTitle>
            <CardDescription>Aún no hay denuncias registradas para tu empresa.</CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <div className="overflow-x-auto rounded-lg border bg-card">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="border-b bg-muted/40 text-left text-muted-foreground">
                <th className="px-4 py-3 font-medium">Fecha</th>
                <th className="px-4 py-3 font-medium">Categoría</th>
                <th className="px-4 py-3 font-medium">Asunto</th>
                <th className="px-4 py-3 font-medium">Descripción</th>
                <th className="px-4 py-3 font-medium w-[220px]">Estatus / Acciones</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-b last:border-0 align-top">
                  <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{formatCreatedAt(row.createdAt)}</td>
                  <td className="px-4 py-3">
                    <span className="text-foreground">{CATEGORY_LABEL[row.category] ?? row.category}</span>
                  </td>
                  <td className="px-4 py-3 max-w-[200px]">
                    <span className="font-medium line-clamp-3">{row.subject}</span>
                  </td>
                  <td className="px-4 py-3 max-w-[280px]">
                    <p className="text-muted-foreground line-clamp-4 whitespace-pre-wrap">{row.description}</p>
                  </td>
                  <td className="px-4 py-3 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant={STATUS_VARIANT[row.status]} className={STATUS_BADGE_CLASS[row.status]}>
                        {STATUS_LABEL[row.status]}
                      </Badge>
                    </div>
                    <select
                      className={selectClassName}
                      value={row.status}
                      disabled={!canWriteCompanyData || updatingId === row.id}
                      onChange={(ev) =>
                        handleStatusChange(row.id, ev.target.value as AnonymousComplaintStatus)
                      }
                      aria-label={`Estatus de ${row.subject}`}
                    >
                      {(Object.keys(STATUS_LABEL) as AnonymousComplaintStatus[]).map((s) => (
                        <option key={s} value={s}>
                          {STATUS_LABEL[s]}
                        </option>
                      ))}
                    </select>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedComplaint(row);
                        setIsDialogOpen(true);
                      }}
                      aria-label={`Ver denuncia: ${row.subject}`}
                      className="w-fit"
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Ver mensaje
                    </Button>
                    {rowErrors[row.id] && (
                      <p className="text-xs text-destructive">{rowErrors[row.id]}</p>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Dialog
        open={isDialogOpen}
        onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) {
            setSelectedComplaint(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedComplaint?.subject ?? 'Detalle de denuncia'}</DialogTitle>
          </DialogHeader>
          {selectedComplaint && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline">{CATEGORY_LABEL[selectedComplaint.category] ?? selectedComplaint.category}</Badge>
                <Badge variant="secondary">{formatCreatedAt(selectedComplaint.createdAt)}</Badge>
                <Badge
                  variant={STATUS_VARIANT[selectedComplaint.status]}
                  className={STATUS_BADGE_CLASS[selectedComplaint.status]}
                >
                  {STATUS_LABEL[selectedComplaint.status]}
                </Badge>
              </div>
              <div className="rounded-md border bg-muted/20 p-4 max-h-[60vh] overflow-y-auto">
                <p className="text-sm whitespace-pre-wrap leading-relaxed">{selectedComplaint.description}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
